import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Play, Pause, GitBranch, Users, Clock, Calendar, Copy, List } from 'lucide-react';
import { Card, Button, useToast } from '@caruso/ui';
import { flowsService } from '@/services/flows.service';

export function FlowsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: flows, isLoading } = useQuery({
    queryKey: ['flows'],
    queryFn: () => flowsService.getAll(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => flowsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flows'] });
      toast({
        title: 'Fluxo excluído!',
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Erro ao excluir fluxo',
        variant: 'destructive',
      });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => flowsService.duplicate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flows'] });
      toast({
        title: 'Fluxo duplicado com sucesso!',
        description: 'O fluxo foi criado como inativo. Ative-o quando estiver pronto.',
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Erro ao duplicar fluxo',
        variant: 'destructive',
      });
    },
  });

  const handleDelete = (id: string, name: string) => {
    if (confirm(`Tem certeza que deseja excluir o fluxo "${name}"?`)) {
      deleteMutation.mutate(id);
    }
  };

  const handleDuplicate = (id: string, name: string) => {
    if (confirm(`Deseja duplicar o fluxo "${name}"?`)) {
      duplicateMutation.mutate(id);
    }
  };

  const getDaysText = (allowedDays?: string) => {
    if (!allowedDays) return 'Todos os dias';
    try {
      const days = JSON.parse(allowedDays);
      const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
      return days.map((d: number) => dayNames[d % 7]).join(', ');
    } catch {
      return 'Todos os dias';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white">Fluxos de Atendimento</h1>
          <p className="text-brand-ivory/80 mt-2 text-lg">Configure e gerencie fluxos automatizados de WhatsApp</p>
        </div>
        <Button onClick={() => navigate('/app/fluxos/novo')} className="gap-2">
          <Plus className="w-5 h-5" />
          Novo Fluxo
        </Button>
      </div>

      {/* Flows List */}
      {isLoading ? (
        <div className="grid grid-cols-1 gap-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="p-6 bg-brand-smoke border-brand-gold/20 animate-pulse">
              <div className="h-20 bg-brand-ink/50 rounded"></div>
            </Card>
          ))}
        </div>
      ) : flows && flows.length > 0 ? (
        <div className="grid grid-cols-1 gap-4">
          {flows.map((flow) => (
            <Card key={flow.id} className="p-6 bg-brand-smoke border-brand-gold/20 hover:border-brand-gold/40 transition-all cursor-pointer">
              <div className="flex items-start justify-between" onClick={() => navigate(`/app/fluxos/${flow.id}/editar`)}>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-xl font-bold text-white">{flow.name}</h3>
                    {flow.active ? (
                      <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-500/20 text-green-300">
                        Ativo
                      </span>
                    ) : (
                      <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-500/20 text-gray-300">
                        Inativo
                      </span>
                    )}
                  </div>

                  {flow.description && (
                    <p className="text-brand-ivory/70 mb-4">{flow.description}</p>
                  )}

                  <div className="flex flex-wrap gap-4 text-sm">
                    {flow.list && (
                      <div className="flex items-center gap-2 text-brand-ivory/70">
                        <Users className="w-4 h-4 text-brand-gold" />
                        <span>{flow.list.name}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-brand-ivory/70">
                      <Calendar className="w-4 h-4 text-brand-gold" />
                      <span>{getDaysText(flow.allowedDays)}</span>
                    </div>
                    {flow.startTime && flow.endTime && (
                      <div className="flex items-center gap-2 text-brand-ivory/70">
                        <Clock className="w-4 h-4 text-brand-gold" />
                        <span>{flow.startTime} - {flow.endTime}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-brand-ivory/70">
                      <GitBranch className="w-4 h-4 text-brand-gold" />
                      <span>{flow._count?.executions || 0} execuções</span>
                    </div>
                  </div>

                  <div className="flex gap-3 mt-4 text-xs text-brand-ivory/60">
                    <span>Intervalo mensagens: {flow.delayBetweenMessages}s</span>
                    <span>•</span>
                    <span>Intervalo clientes: {flow.delayBetweenClients}s</span>
                  </div>
                </div>

                <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate(`/app/fluxos/${flow.id}/executar`)}
                    title="Executar fluxo"
                  >
                    <Play className="w-5 h-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate(`/app/fluxos/${flow.id}/execucoes-lista`)}
                    title="Ver execuções"
                    className="text-brand-gold hover:bg-brand-gold/10"
                  >
                    <List className="w-5 h-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDuplicate(flow.id, flow.name)}
                    disabled={duplicateMutation.isPending}
                    title="Duplicar fluxo"
                    className="text-brand-gold hover:bg-brand-gold/10"
                  >
                    <Copy className="w-5 h-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate(`/app/fluxos/${flow.id}/editar`)}
                    title="Editar"
                  >
                    <Edit className="w-5 h-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(flow.id, flow.name)}
                    disabled={deleteMutation.isPending}
                    title="Excluir"
                    className="text-red-400 hover:bg-red-400/10"
                  >
                    <Trash2 className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-12 bg-brand-smoke border-brand-gold/20 text-center">
          <GitBranch className="w-16 h-16 text-brand-gold/50 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Nenhum fluxo encontrado</h3>
          <p className="text-brand-ivory/60 mb-6">Comece criando seu primeiro fluxo de atendimento</p>
          <Button onClick={() => navigate('/app/fluxos/novo')}>
            <Plus className="w-5 h-5 mr-2" />
            Criar Primeiro Fluxo
          </Button>
        </Card>
      )}
    </div>
  );
}
